import React, { useState, useEffect, useCallback, useRef } from 'react';
import { billingService, posService, restaurantService, orderService } from '../../services/dataService';
import { invBillingIntegration } from '../../services/inventoryService';
import { calculateBill, isValidOverride, hasOverride } from '../../utils/billingCalc';
import InvoicePrint from './InvoicePrint';
import CustomerPicker from './CustomerPicker';

const fmt = (n) => `₹${(n || 0).toFixed(2)}`;

const card = {
  background: 'rgba(255,255,255,0.95)', borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid rgba(0,0,0,0.06)',
};

const btn = (variant = 'primary', disabled = false) => {
  const base = { border: 'none', borderRadius: '10px', padding: '10px 20px', fontSize: '0.92rem', fontWeight: '700', cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.2s' };
  if (disabled) return { ...base, background: '#e5e7eb', color: '#9ca3af' };
  if (variant === 'primary') return { ...base, background: 'linear-gradient(135deg, #FF4444, #FF6B00)', color: 'white', boxShadow: '0 3px 10px rgba(255,68,68,0.3)' };
  if (variant === 'green') return { ...base, background: '#16a34a', color: 'white' };
  if (variant === 'outline') return { ...base, background: 'transparent', color: '#666', border: '2px solid #e5e7eb' };
  return base;
};

const inputStyle = (hasError = false) => ({
  width: '100%', padding: '10px 12px',
  border: `2px solid ${hasError ? '#dc2626' : '#e5e7eb'}`,
  borderRadius: '10px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box',
});

const PAYMENT_METHODS = ['cash', 'card', 'upi', 'other'];
const DISCOUNT_TYPES = ['NONE', 'FIXED', 'PERCENT'];

const friendlyErr = (err) => {
  if (!err) return 'Something went wrong. Please try again.';
  const msg = String(err.message || err).toLowerCase();
  if (msg.includes('fetch') || msg.includes('network')) return 'Network error — check your internet connection.';
  if (msg.includes('jwt') || msg.includes('expired') || msg.includes('auth')) return 'Session expired — please refresh and log in again.';
  if (msg.includes('policy') || msg.includes('permission') || msg.includes('rls')) return "You don't have permission to do this.";
  if (msg.includes('duplicate') || msg.includes('unique')) return 'This record already exists.';
  if (msg.includes('foreign key') || msg.includes('violates foreign')) return 'This record is in use and cannot be removed.';
  if (msg.includes('timeout') || msg.includes('timed out')) return 'Request timed out — please try again.';
  return 'Something went wrong. Please try again.';
};

// ─── Billing Panel ─────────────────────────────────────────────────────────
const BillingPanel = ({ orders, menu, settings, restaurantId, timezone, managerId, onInvoiceGenerated, onError }) => {
  const primaryOrder = orders[0];
  const isMerge = orders.length > 1;

  const defaultGst = settings?.gst_enabled ? parseFloat(settings.gst_percent || 0) : 0;
  const defaultSc  = settings?.service_charge_enabled ? parseFloat(settings.service_charge_percent || 0) : 0;
  const defaultDiscountType  = settings?.discount_enabled ? (settings.discount_type || 'NONE') : 'NONE';
  const defaultDiscountValue = settings?.discount_enabled ? parseFloat(settings.discount_value || 0) : 0;

  const [appliedGst, setAppliedGst]   = useState(String(defaultGst));
  const [appliedSc, setAppliedSc]     = useState(String(defaultSc));
  const [discountType, setDiscountType] = useState(defaultDiscountType);
  const [discountValue, setDiscountValue] = useState(String(defaultDiscountValue));
  const [fieldErrors, setFieldErrors] = useState({});
  const [billing, setBilling] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [invoicePaymentMode, setInvoicePaymentMode] = useState('unpaid');

  // Item editor state
  const [editedItems, setEditedItems] = useState([]);
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [pickerCategory, setPickerCategory] = useState(null);

  const orderKey = orders.map(o => o.id).join(',');

  // Track whether billing defaults have been seeded for the current order
  // Prevents the 20-second settings poll from resetting user-edited values
  const billingSeededRef = useRef(false);

  // Re-seed when selected orders change
  useEffect(() => {
    const combined = orders.flatMap(o =>
      (o.order_items || []).map(oi => ({
        menu_item_id: oi.menu_items?.id,
        name: oi.menu_items?.name || 'Item',
        unit_price: oi.unit_price,
        quantity: oi.quantity,
      }))
    );
    setEditedItems(combined);
    setShowItemPicker(false);
    setPickerCategory(null);
    setAppliedGst(String(defaultGst));
    setAppliedSc(String(defaultSc));
    setDiscountType(defaultDiscountType);
    setDiscountValue(String(defaultDiscountValue));
    setFieldErrors({});
    setSelectedCustomer(null);
    setInvoicePaymentMode('unpaid');
    billingSeededRef.current = !!settings; // if settings already loaded, mark as seeded
  }, [orderKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seed billing fields only when settings first loads (null → value).
  // After that, ignore updates so user-edited values (e.g. custom discount) are preserved.
  // Customer discount takes priority — don't overwrite it if a customer is already selected.
  useEffect(() => {
    if (!settings || billingSeededRef.current) return;
    billingSeededRef.current = true;
    setAppliedGst(String(settings.gst_enabled ? parseFloat(settings.gst_percent || 0) : 0));
    setAppliedSc(String(settings.service_charge_enabled ? parseFloat(settings.service_charge_percent || 0) : 0));
    if (!(selectedCustomer?.discount_value > 0)) {
      setDiscountType(settings.discount_enabled ? (settings.discount_type || 'NONE') : 'NONE');
      setDiscountValue(String(settings.discount_enabled ? parseFloat(settings.discount_value || 0) : 0));
    }
  }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps

  // Item editor helpers
  const setQty = (idx, delta) => {
    setEditedItems(prev => {
      const next = [...prev];
      const newQty = next[idx].quantity + delta;
      if (newQty <= 0) return prev.filter((_, i) => i !== idx);
      next[idx] = { ...next[idx], quantity: newQty };
      return next;
    });
  };

  const removeItem = (idx) => setEditedItems(prev => prev.filter((_, i) => i !== idx));

  const addFromMenu = (menuItem) => {
    setEditedItems(prev => {
      const existing = prev.findIndex(i => i.menu_item_id === menuItem.id);
      if (existing >= 0) {
        const next = [...prev];
        next[existing] = { ...next[existing], quantity: next[existing].quantity + 1 };
        return next;
      }
      return [...prev, { menu_item_id: menuItem.id, name: menuItem.name, unit_price: menuItem.price, quantity: 1 }];
    });
  };

  const gstEnabled = settings?.gst_enabled && parseFloat(appliedGst) > 0;
  const scEnabled  = settings?.service_charge_enabled && parseFloat(appliedSc) > 0;

  const calc = calculateBill({
    items: editedItems.map(i => ({ unit_price: i.unit_price, quantity: i.quantity })),
    discountType,
    discountValue: parseFloat(discountValue) || 0,
    gstEnabled,
    appliedGstPercent: parseFloat(appliedGst) || 0,
    serviceChargeEnabled: scEnabled,
    appliedScPercent: parseFloat(appliedSc) || 0,
  });

  const overrideDetected = hasOverride({
    appliedGstPercent: parseFloat(appliedGst) || 0,
    defaultGstPercent: defaultGst,
    appliedScPercent: parseFloat(appliedSc) || 0,
    defaultScPercent: defaultSc,
  });

  const validate = () => {
    const e = {};
    if (!isValidOverride(appliedGst, defaultGst)) e.gst = `Max allowed: ${defaultGst}%`;
    if (!isValidOverride(appliedSc, defaultSc)) e.sc = `Max allowed: ${defaultSc}%`;
    if (discountType === 'PERCENT') {
      const v = parseFloat(discountValue) || 0;
      if (v < 0 || v > 100) e.discount = 'Discount % must be 0–100.';
    }
    if (discountType === 'FIXED') {
      const v = parseFloat(discountValue) || 0;
      if (v < 0) e.discount = 'Discount amount must be ≥ 0.';
    }
    if (editedItems.length === 0) e.items = 'Add at least one item.';
    return e;
  };

  const generateInvoice = async () => {
    const e = validate();
    if (Object.keys(e).length) { setFieldErrors(e); return; }
    if (settings?.gst_enabled && !settings?.gstin?.trim()) {
      onError('GSTIN is required to generate a Tax Invoice. Add it in Billing Settings.');
      return;
    }
    setBilling(true); onError('');

    // 1. Persist edited items to primary order
    const { error: itemsErr } = await posService.updateOrderItems(primaryOrder.id, editedItems);
    if (itemsErr) { onError(friendlyErr(itemsErr)); setBilling(false); return; }

    // 2. Claim invoice number first — needed to stamp on secondary orders
    const { invoiceNumber, error: invErr } = await billingService.claimNextInvoiceNumber(restaurantId, timezone);
    if (invErr) { onError('Failed to generate invoice number.'); setBilling(false); return; }

    // 3. Mark secondary orders as merged, stamping the invoice_number so they can be
    //    found by invoice at print time (avoids needing a separate merged_order_numbers column)
    for (const order of orders.slice(1)) {
      const { error: mergeErr } = await posService.markAsMerged(order.id, invoiceNumber);
      if (mergeErr) { onError(friendlyErr(mergeErr)); setBilling(false); return; }
    }

    // 4. Bill primary order
    const { error: billErr } = await posService.billOrder(primaryOrder.id, {
      ...calc,
      invoiceNumber,
      discountType,
      appliedGstPercent: parseFloat(appliedGst) || 0,
      appliedScPercent: parseFloat(appliedSc) || 0,
      isOverride: overrideDetected,
      originalGstPercent: defaultGst,
      originalScPercent: defaultSc,
      customerId: selectedCustomer?.id || null,
      customerName: selectedCustomer?.name || null,
      customerPhone: selectedCustomer?.phone || null,
      managerId: managerId || null,
    });

    setBilling(false);
    if (billErr) { onError(friendlyErr(billErr)); return; }
    if (invoicePaymentMode !== 'unpaid') {
      await posService.markPaid(primaryOrder.id, invoicePaymentMode);
    }
    // Deduct inventory stock + auto-log recipe wastage for all orders in this invoice
    for (const order of orders) {
      invBillingIntegration.deductForOrder(restaurantId, order.id).catch(() => {});
    }
    onInvoiceGenerated();
  };

  // Table label for header
  const tableLabel = isMerge
    ? orders.map(o => o.restaurant_tables?.custom_name || `Table ${o.restaurant_tables?.table_number || '?'}`).join(' + ')
    : primaryOrder.order_type === 'TAKEAWAY'
      ? '🛍 Takeaway'
      : (primaryOrder.restaurant_tables?.custom_name || `Table ${primaryOrder.restaurant_tables?.table_number || '?'}`);

  // Flatten menu into item list for picker
  const allCategories = menu || [];
  const activePickerCategory = pickerCategory || allCategories[0]?.id;
  const pickerItems = allCategories.find(c => c.id === activePickerCategory)?.menu_items?.filter(i => i.is_available !== false) || [];

  return (
    <div style={{ ...card, padding: '24px' }}>
      <h3 style={{ margin: '0 0 4px 0', fontSize: '1.1rem', fontWeight: '700', color: '#333' }}>
        Billing Breakdown
        <span style={{ marginLeft: '10px', fontSize: '0.75rem', color: isMerge ? '#FF6B00' : '#888', fontWeight: isMerge ? '700' : '400' }}>
          {tableLabel}
        </span>
      </h3>
      {isMerge && (
        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '6px 12px', marginBottom: '14px', fontSize: '0.82rem', color: '#9a3412', fontWeight: '600' }}>
          Merging {orders.length} orders — one invoice will be generated
        </div>
      )}

      <CustomerPicker
        restaurantId={restaurantId}
        value={selectedCustomer}
        onChange={(customer) => {
          setSelectedCustomer(customer);
          if (customer && customer.discount_type !== 'NONE' && customer.discount_value > 0) {
            setDiscountType(customer.discount_type === 'FLAT' ? 'FIXED' : customer.discount_type);
            setDiscountValue(String(customer.discount_value));
          }
        }}
        disabled={billing}
      />

      {/* Editable item list */}
      <div style={{ marginBottom: '12px' }}>
        {editedItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '16px 0', color: '#aaa', fontSize: '0.85rem' }}>No items — add from menu below</div>
        ) : (
          editedItems.map((item, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f3f4f6', gap: '8px' }}>
              <span style={{ flex: 1, color: '#555', fontSize: '0.9rem' }}>{item.name}</span>
              {/* Qty stepper */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button onClick={() => setQty(idx, -1)} style={{ width: '24px', height: '24px', border: '1px solid #e5e7eb', borderRadius: '6px', background: 'white', cursor: 'pointer', fontWeight: '700', fontSize: '0.9rem', color: '#555' }}>−</button>
                <span style={{ width: '24px', textAlign: 'center', fontSize: '0.9rem', fontWeight: '600' }}>{item.quantity}</span>
                <button onClick={() => setQty(idx, 1)} style={{ width: '24px', height: '24px', border: '1px solid #e5e7eb', borderRadius: '6px', background: 'white', cursor: 'pointer', fontWeight: '700', fontSize: '0.9rem', color: '#555' }}>+</button>
              </div>
              <span style={{ width: '70px', textAlign: 'right', fontWeight: '600', fontSize: '0.9rem' }}>{fmt(item.unit_price * item.quantity)}</span>
              <button onClick={() => removeItem(idx)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '1rem', padding: '0 4px', fontWeight: '700' }}>×</button>
            </div>
          ))
        )}
        {fieldErrors.items && <p style={{ margin: '4px 0 0', color: '#dc2626', fontSize: '0.78rem' }}>{fieldErrors.items}</p>}

        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0', fontWeight: '700', fontSize: '0.95rem', color: '#333' }}>
          <span>Gross Total</span><span>{fmt(calc.gross)}</span>
        </div>
      </div>

      {/* Add Item toggle */}
      <button
        onClick={() => setShowItemPicker(p => !p)}
        style={{ ...btn('outline'), width: '100%', marginBottom: '14px', fontSize: '0.85rem', padding: '8px' }}
      >
        {showItemPicker ? '▲ Close item picker' : '＋ Add Item'}
      </button>

      {/* Item Picker */}
      {showItemPicker && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', marginBottom: '14px', overflow: 'hidden' }}>
          {/* Category tabs */}
          <div style={{ display: 'flex', overflowX: 'auto', gap: '0', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
            {allCategories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setPickerCategory(cat.id)}
                style={{
                  flexShrink: 0, padding: '8px 14px', border: 'none', cursor: 'pointer',
                  fontWeight: '600', fontSize: '0.8rem', whiteSpace: 'nowrap',
                  background: activePickerCategory === cat.id ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : 'transparent',
                  color: activePickerCategory === cat.id ? 'white' : '#555',
                }}
              >
                {cat.name}
              </button>
            ))}
          </div>
          {/* Items */}
          <div style={{ maxHeight: '200px', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '8px' }}>
            {pickerItems.length === 0 ? (
              <div style={{ color: '#aaa', textAlign: 'center', padding: '16px', fontSize: '0.85rem' }}>No items in this category</div>
            ) : (
              pickerItems.map(item => (
                <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', borderRadius: '6px', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ fontSize: '0.88rem', color: '#333' }}>{item.name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '0.82rem', color: '#888' }}>{fmt(item.price)}</span>
                    <button
                      onClick={() => addFromMenu(item)}
                      style={{ padding: '3px 10px', border: 'none', borderRadius: '6px', background: '#FF6B00', color: 'white', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer' }}
                    >
                      Add
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Override notice */}
      {overrideDetected && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '8px 12px', marginBottom: '14px', fontSize: '0.82rem', color: '#92400e', fontWeight: '600' }}>
          ⚠️ Override active — changes from restaurant defaults will be logged.
        </div>
      )}

      {/* Discount */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: '#555', marginBottom: '5px' }}>Discount</label>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
          {DISCOUNT_TYPES.map(t => (
            <button
              key={t}
              onClick={() => { setDiscountType(t); if (t === 'NONE') setDiscountValue('0'); setFieldErrors(prev => ({ ...prev, discount: undefined })); }}
              style={{
                flex: 1, padding: '7px 4px', borderRadius: '8px', fontWeight: '600', fontSize: '0.8rem', cursor: 'pointer',
                background: discountType === t ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : 'transparent',
                color: discountType === t ? 'white' : '#555',
                border: discountType === t ? 'none' : '2px solid #e5e7eb',
              }}
            >{t === 'NONE' ? 'No Discount' : t === 'FIXED' ? '₹ Fixed' : '% Percent'}</button>
          ))}
        </div>
        {discountType !== 'NONE' && (
          <>
            <input
              type="number" min="0" max={discountType === 'PERCENT' ? 100 : undefined} step="1"
              value={discountValue}
              onChange={e => { setDiscountValue(e.target.value); setFieldErrors(prev => ({ ...prev, discount: undefined })); }}
              placeholder={discountType === 'PERCENT' ? 'e.g. 10' : 'e.g. 50'}
              style={inputStyle(!!fieldErrors.discount)}
              onFocus={e => e.target.style.borderColor = '#FF6B00'}
              onBlur={e => e.target.style.borderColor = fieldErrors.discount ? '#dc2626' : '#e5e7eb'}
            />
            {fieldErrors.discount && <p style={{ margin: '3px 0 0', color: '#dc2626', fontSize: '0.78rem' }}>{fieldErrors.discount}</p>}
          </>
        )}
      </div>

      {/* Editable overrides */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
        {settings?.gst_enabled && (
          <div>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: '#555', marginBottom: '5px' }}>
              GST % <span style={{ color: '#888', fontWeight: '400' }}>(default {defaultGst}%)</span>
            </label>
            <input
              type="number" min="0" max={defaultGst} step="0.5" value={appliedGst}
              onChange={e => { setAppliedGst(e.target.value); setFieldErrors(prev => ({ ...prev, gst: undefined })); }}
              style={inputStyle(!!fieldErrors.gst)}
              onFocus={e => e.target.style.borderColor = '#FF6B00'}
              onBlur={e => e.target.style.borderColor = fieldErrors.gst ? '#dc2626' : '#e5e7eb'}
            />
            {fieldErrors.gst && <p style={{ margin: '3px 0 0', color: '#dc2626', fontSize: '0.78rem' }}>{fieldErrors.gst}</p>}
          </div>
        )}
        {settings?.service_charge_enabled && (
          <div>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: '#555', marginBottom: '5px' }}>
              Service Charge % <span style={{ color: '#888', fontWeight: '400' }}>(default {defaultSc}%)</span>
            </label>
            <input
              type="number" min="0" max={defaultSc} step="0.5" value={appliedSc}
              onChange={e => { setAppliedSc(e.target.value); setFieldErrors(prev => ({ ...prev, sc: undefined })); }}
              style={inputStyle(!!fieldErrors.sc)}
              onFocus={e => e.target.style.borderColor = '#FF6B00'}
              onBlur={e => e.target.style.borderColor = fieldErrors.sc ? '#dc2626' : '#e5e7eb'}
            />
            {fieldErrors.sc && <p style={{ margin: '3px 0 0', color: '#dc2626', fontSize: '0.78rem' }}>{fieldErrors.sc}</p>}
          </div>
        )}
      </div>

      {/* Totals */}
      <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '12px', marginBottom: '16px' }}>
        {[
          calc.discountAmount > 0 && [
            discountType === 'PERCENT' ? `Discount (${discountValue}%)` : 'Discount (₹)',
            -calc.discountAmount
          ],
          (calc.gross !== calc.taxableAmount) && ['Taxable Amount', calc.taxableAmount],
          settings?.gst_enabled && gstEnabled && [`GST (${appliedGst}%)`, calc.gstAmount],
          settings?.service_charge_enabled && scEnabled && [`Service Charge (${appliedSc}%)`, calc.serviceChargeAmount],
        ].filter(Boolean).map(([label, amount]) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '0.9rem', color: '#555' }}>
            <span>{label}</span>
            <span style={{ fontWeight: '600', color: amount < 0 ? '#16a34a' : '#333' }}>
              {amount < 0 ? `−${fmt(-amount)}` : fmt(amount)}
            </span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0', borderTop: '2px solid #333', fontWeight: '800', fontSize: '1.1rem', color: '#111' }}>
          <span>Total Payable</span><span>{fmt(calc.totalPayable)}</span>
        </div>
      </div>

      {/* Payment mode selector */}
      <div style={{ marginBottom: '14px' }}>
        <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#555', marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Payment Mode
        </label>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {['unpaid', 'cash', 'card', 'upi', 'other'].map(m => (
            <button
              key={m}
              onClick={() => setInvoicePaymentMode(m)}
              disabled={billing}
              style={{
                flex: 1, minWidth: '60px', padding: '8px 6px', borderRadius: '8px', fontWeight: '700', fontSize: '0.78rem', cursor: billing ? 'not-allowed' : 'pointer',
                background: invoicePaymentMode === m ? (m === 'unpaid' ? '#6b7280' : 'linear-gradient(135deg, #FF4444, #FF6B00)') : 'transparent',
                color: invoicePaymentMode === m ? 'white' : '#555',
                border: invoicePaymentMode === m ? 'none' : '2px solid #e5e7eb',
              }}
            >
              {m === 'unpaid' ? 'Unpaid' : m.toUpperCase()}
            </button>
          ))}
        </div>
        {invoicePaymentMode !== 'unpaid' && (
          <div style={{ marginTop: '6px', fontSize: '0.75rem', color: '#16a34a', fontWeight: '600' }}>
            Invoice will be generated and marked paid via {invoicePaymentMode.toUpperCase()}
          </div>
        )}
      </div>

      <button onClick={generateInvoice} disabled={billing} style={{ ...btn('primary', billing), width: '100%', padding: '14px' }}>
        {billing ? 'Generating...' : isMerge ? `Merge & Generate Invoice` : 'Generate Invoice'}
      </button>
    </div>
  );
};

// ─── New Order Panel ────────────────────────────────────────────────────────
const NewOrderPanel = ({ menu, tables, settings, restaurantId, timezone, restaurantName, managerId, onDone, onError }) => {
  const defaultGst = settings?.gst_enabled ? parseFloat(settings.gst_percent || 0) : 0;
  const defaultSc  = settings?.service_charge_enabled ? parseFloat(settings.service_charge_percent || 0) : 0;
  const defaultDiscountType  = settings?.discount_enabled ? (settings.discount_type || 'NONE') : 'NONE';
  const defaultDiscountValue = settings?.discount_enabled ? parseFloat(settings.discount_value || 0) : 0;

  const [orderType, setOrderType] = useState('DINE_IN');
  const [tableId, setTableId] = useState('');
  const [editedItems, setEditedItems] = useState([]);
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [pickerCategory, setPickerCategory] = useState(null);
  const [appliedGst, setAppliedGst]   = useState(String(defaultGst));
  const [appliedSc, setAppliedSc]     = useState(String(defaultSc));
  const [discountType, setDiscountType] = useState(defaultDiscountType);
  const [discountValue, setDiscountValue] = useState(String(defaultDiscountValue));
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [invoicePaymentMode, setInvoicePaymentMode] = useState('unpaid');

  // Re-seed billing fields when settings first loads (settings is null on first render)
  useEffect(() => {
    if (!settings) return;
    setAppliedGst(String(settings.gst_enabled ? parseFloat(settings.gst_percent || 0) : 0));
    setAppliedSc(String(settings.service_charge_enabled ? parseFloat(settings.service_charge_percent || 0) : 0));
    setDiscountType(settings.discount_enabled ? (settings.discount_type || 'NONE') : 'NONE');
    setDiscountValue(String(settings.discount_enabled ? parseFloat(settings.discount_value || 0) : 0));
  }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps

  const setQty = (idx, delta) => setEditedItems(prev => {
    const next = [...prev];
    const newQty = next[idx].quantity + delta;
    if (newQty <= 0) return prev.filter((_, i) => i !== idx);
    next[idx] = { ...next[idx], quantity: newQty };
    return next;
  });

  const addFromMenu = (item) => setEditedItems(prev => {
    const existing = prev.findIndex(i => i.menu_item_id === item.id);
    if (existing >= 0) {
      const next = [...prev];
      next[existing] = { ...next[existing], quantity: next[existing].quantity + 1 };
      return next;
    }
    return [...prev, { menu_item_id: item.id, name: item.name, unit_price: item.price, quantity: 1 }];
  });

  const gstEnabled = settings?.gst_enabled && parseFloat(appliedGst) > 0;
  const scEnabled  = settings?.service_charge_enabled && parseFloat(appliedSc) > 0;

  const calc = calculateBill({
    items: editedItems.map(i => ({ unit_price: i.unit_price, quantity: i.quantity })),
    discountType,
    discountValue: parseFloat(discountValue) || 0,
    gstEnabled,
    appliedGstPercent: parseFloat(appliedGst) || 0,
    serviceChargeEnabled: scEnabled,
    appliedScPercent: parseFloat(appliedSc) || 0,
  });

  const overrideDetected = hasOverride({
    appliedGstPercent: parseFloat(appliedGst) || 0,
    defaultGstPercent: defaultGst,
    appliedScPercent: parseFloat(appliedSc) || 0,
    defaultScPercent: defaultSc,
  });

  const allCategories = menu || [];
  const activePickerCat = pickerCategory || allCategories[0]?.id;
  const pickerItems = allCategories.find(c => c.id === activePickerCat)?.menu_items?.filter(i => i.is_available !== false) || [];

  const createAndInvoice = async () => {
    setLocalError('');
    const e = {};
    if (editedItems.length === 0) e.items = 'Add at least one item.';
    if (!isValidOverride(appliedGst, defaultGst)) e.gst = `Max allowed: ${defaultGst}%`;
    if (!isValidOverride(appliedSc, defaultSc)) e.sc = `Max allowed: ${defaultSc}%`;
    if (discountType === 'PERCENT' && ((parseFloat(discountValue) || 0) < 0 || (parseFloat(discountValue) || 0) > 100)) e.discount = 'Discount % must be 0–100.';
    if (Object.keys(e).length) { setFieldErrors(e); return; }
    if (settings?.gst_enabled && !settings?.gstin?.trim()) {
      setLocalError('GSTIN is required to generate a Tax Invoice. Add it in Billing Settings.');
      return;
    }
    setSubmitting(true);
    try {
      // 1. Create confirmed counter order with items
      const { data: order, error: orderErr } = await posService.createCounterOrder(restaurantId, {
        tableId: tableId || null,
        orderType,
        items: editedItems,
        notes: '',
      });
      if (orderErr) { setLocalError(friendlyErr(orderErr)); setSubmitting(false); return; }

      // 2. Claim invoice number
      const { invoiceNumber, error: invErr } = await billingService.claimNextInvoiceNumber(restaurantId, timezone);
      if (invErr) { setLocalError('Failed to generate invoice number.'); setSubmitting(false); return; }

      // 3. Bill the order
      const { error: billErr } = await posService.billOrder(order.id, {
        ...calc,
        invoiceNumber,
        discountType,
        appliedGstPercent: parseFloat(appliedGst) || 0,
        appliedScPercent: parseFloat(appliedSc) || 0,
        isOverride: overrideDetected,
        originalGstPercent: defaultGst,
        originalScPercent: defaultSc,
        customerId: selectedCustomer?.id || null,
        customerName: selectedCustomer?.name || null,
        customerPhone: selectedCustomer?.phone || null,
        managerId: managerId || null,
      });
      if (billErr) { setLocalError(friendlyErr(billErr)); setSubmitting(false); return; }
      if (invoicePaymentMode !== 'unpaid') {
        await posService.markPaid(order.id, invoicePaymentMode);
      }
      // Deduct inventory stock + auto-log recipe wastage
      invBillingIntegration.deductForOrder(restaurantId, order.id).catch(() => {});
      // Desktop: order is in SQLite (not yet synced to Supabase) — fetch locally
      const { data: completeOrder } = window.menuronDesktop?.isDesktop
        ? await posService.getOrderById(order.id)
        : await orderService.getOrderById(order.id);
      onDone(completeOrder);
    } catch (err) {
      setLocalError(friendlyErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ ...card, padding: '24px' }}>
      <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', fontWeight: '700', color: '#333' }}>New Counter Order</h3>

      {/* Order type + table */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        {['DINE_IN', 'TAKEAWAY'].map(t => (
          <button key={t} onClick={() => { setOrderType(t); if (t === 'TAKEAWAY') setTableId(''); }}
            style={{ flex: 1, padding: '8px', borderRadius: '8px', fontWeight: '600', fontSize: '0.82rem', cursor: 'pointer', border: orderType === t ? 'none' : '2px solid #e5e7eb', background: orderType === t ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : 'transparent', color: orderType === t ? 'white' : '#555' }}>
            {t === 'DINE_IN' ? 'Dine In' : 'Takeaway'}
          </button>
        ))}
      </div>
      {orderType === 'DINE_IN' && (
        <select value={tableId} onChange={e => setTableId(e.target.value)}
          style={{ width: '100%', padding: '9px 12px', border: '2px solid #e5e7eb', borderRadius: '10px', fontSize: '0.9rem', outline: 'none', marginBottom: '14px', background: 'white' }}>
          <option value="">No table</option>
          {(tables || []).map(t => (
            <option key={t.id} value={t.id}>{t.custom_name || `Table ${t.table_number}`}</option>
          ))}
        </select>
      )}

      <CustomerPicker
        restaurantId={restaurantId}
        value={selectedCustomer}
        onChange={(customer) => {
          setSelectedCustomer(customer);
          if (customer && customer.discount_type !== 'NONE' && customer.discount_value > 0) {
            setDiscountType(customer.discount_type === 'FLAT' ? 'FIXED' : customer.discount_type);
            setDiscountValue(String(customer.discount_value));
          }
        }}
        disabled={submitting}
      />

      {/* Item list */}
      <div style={{ marginBottom: '8px' }}>
        {editedItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '14px 0', color: '#aaa', fontSize: '0.85rem' }}>No items yet — add from menu below</div>
        ) : (
          editedItems.map((item, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #f3f4f6', gap: '8px' }}>
              <span style={{ flex: 1, color: '#555', fontSize: '0.88rem' }}>{item.name}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button onClick={() => setQty(idx, -1)} style={{ width: '22px', height: '22px', border: '1px solid #e5e7eb', borderRadius: '5px', background: 'white', cursor: 'pointer', fontWeight: '700', fontSize: '0.85rem' }}>−</button>
                <span style={{ width: '22px', textAlign: 'center', fontSize: '0.85rem', fontWeight: '600' }}>{item.quantity}</span>
                <button onClick={() => setQty(idx, 1)} style={{ width: '22px', height: '22px', border: '1px solid #e5e7eb', borderRadius: '5px', background: 'white', cursor: 'pointer', fontWeight: '700', fontSize: '0.85rem' }}>+</button>
              </div>
              <span style={{ width: '64px', textAlign: 'right', fontWeight: '600', fontSize: '0.85rem' }}>{fmt(item.unit_price * item.quantity)}</span>
              <button onClick={() => setEditedItems(p => p.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '1rem', padding: '0 2px', fontWeight: '700' }}>×</button>
            </div>
          ))
        )}
        {fieldErrors.items && <p style={{ margin: '4px 0 0', color: '#dc2626', fontSize: '0.78rem' }}>{fieldErrors.items}</p>}
        {editedItems.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0 0', fontWeight: '700', fontSize: '0.9rem', color: '#333' }}>
            <span>Gross Total</span><span>{fmt(calc.gross)}</span>
          </div>
        )}
      </div>

      {/* Add Item picker */}
      <button onClick={() => setShowItemPicker(p => !p)}
        style={{ ...btn('outline'), width: '100%', marginBottom: '12px', fontSize: '0.82rem', padding: '7px' }}>
        {showItemPicker ? '▲ Close picker' : '＋ Add Item'}
      </button>
      {showItemPicker && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', marginBottom: '12px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', overflowX: 'auto', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
            {allCategories.map(cat => (
              <button key={cat.id} onClick={() => setPickerCategory(cat.id)}
                style={{ flexShrink: 0, padding: '7px 12px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.78rem', whiteSpace: 'nowrap', background: activePickerCat === cat.id ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : 'transparent', color: activePickerCat === cat.id ? 'white' : '#555' }}>
                {cat.name}
              </button>
            ))}
          </div>
          <div style={{ maxHeight: '180px', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '6px' }}>
            {pickerItems.map(item => (
              <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 6px', borderRadius: '6px' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ fontSize: '0.85rem', color: '#333' }}>{item.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '0.78rem', color: '#888' }}>{fmt(item.price)}</span>
                  <button onClick={() => addFromMenu(item)} style={{ padding: '2px 9px', border: 'none', borderRadius: '5px', background: '#FF6B00', color: 'white', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer' }}>Add</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Override notice */}
      {overrideDetected && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '7px 10px', marginBottom: '12px', fontSize: '0.8rem', color: '#92400e', fontWeight: '600' }}>
          ⚠️ Override active — will be logged.
        </div>
      )}

      {/* Discount */}
      <div style={{ marginBottom: '14px' }}>
        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '600', color: '#555', marginBottom: '5px' }}>Discount</label>
        <div style={{ display: 'flex', gap: '5px', marginBottom: '6px' }}>
          {DISCOUNT_TYPES.map(t => (
            <button key={t} onClick={() => { setDiscountType(t); if (t === 'NONE') setDiscountValue('0'); setFieldErrors(p => ({ ...p, discount: undefined })); }}
              style={{ flex: 1, padding: '6px 3px', borderRadius: '7px', fontWeight: '600', fontSize: '0.75rem', cursor: 'pointer', background: discountType === t ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : 'transparent', color: discountType === t ? 'white' : '#555', border: discountType === t ? 'none' : '2px solid #e5e7eb' }}>
              {t === 'NONE' ? 'None' : t === 'FIXED' ? '₹ Fixed' : '% Pct'}
            </button>
          ))}
        </div>
        {discountType !== 'NONE' && (
          <input type="number" min="0" value={discountValue}
            onChange={e => { setDiscountValue(e.target.value); setFieldErrors(p => ({ ...p, discount: undefined })); }}
            placeholder={discountType === 'PERCENT' ? 'e.g. 10' : 'e.g. 50'} style={inputStyle(!!fieldErrors.discount)} />
        )}
        {fieldErrors.discount && <p style={{ margin: '3px 0 0', color: '#dc2626', fontSize: '0.75rem' }}>{fieldErrors.discount}</p>}
      </div>

      {/* GST + SC overrides */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
        {settings?.gst_enabled && (
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '600', color: '#555', marginBottom: '4px' }}>GST % (default {defaultGst}%)</label>
            <input type="number" min="0" max={defaultGst} step="0.5" value={appliedGst}
              onChange={e => { setAppliedGst(e.target.value); setFieldErrors(p => ({ ...p, gst: undefined })); }}
              style={inputStyle(!!fieldErrors.gst)} />
            {fieldErrors.gst && <p style={{ margin: '2px 0 0', color: '#dc2626', fontSize: '0.75rem' }}>{fieldErrors.gst}</p>}
          </div>
        )}
        {settings?.service_charge_enabled && (
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '600', color: '#555', marginBottom: '4px' }}>SC % (default {defaultSc}%)</label>
            <input type="number" min="0" max={defaultSc} step="0.5" value={appliedSc}
              onChange={e => { setAppliedSc(e.target.value); setFieldErrors(p => ({ ...p, sc: undefined })); }}
              style={inputStyle(!!fieldErrors.sc)} />
            {fieldErrors.sc && <p style={{ margin: '2px 0 0', color: '#dc2626', fontSize: '0.75rem' }}>{fieldErrors.sc}</p>}
          </div>
        )}
      </div>

      {/* Totals */}
      {editedItems.length > 0 && (
        <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '10px', marginBottom: '14px' }}>
          {[
            calc.discountAmount > 0 && [discountType === 'PERCENT' ? `Discount (${discountValue}%)` : 'Discount (₹)', -calc.discountAmount],
            (calc.gross !== calc.taxableAmount) && ['Taxable Amount', calc.taxableAmount],
            settings?.gst_enabled && gstEnabled && [`GST (${appliedGst}%)`, calc.gstAmount],
            settings?.service_charge_enabled && scEnabled && [`SC (${appliedSc}%)`, calc.serviceChargeAmount],
          ].filter(Boolean).map(([label, amount]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '0.85rem', color: '#555' }}>
              <span>{label}</span>
              <span style={{ fontWeight: '600', color: amount < 0 ? '#16a34a' : '#333' }}>{amount < 0 ? `−${fmt(-amount)}` : fmt(amount)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0', borderTop: '2px solid #333', fontWeight: '800', fontSize: '1.05rem', color: '#111' }}>
            <span>Total Payable</span><span>{fmt(calc.totalPayable)}</span>
          </div>
        </div>
      )}

      {/* Payment mode selector */}
      <div style={{ marginBottom: '14px' }}>
        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#555', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Payment Mode
        </label>
        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
          {['unpaid', 'cash', 'card', 'upi', 'other'].map(m => (
            <button
              key={m}
              onClick={() => setInvoicePaymentMode(m)}
              disabled={submitting}
              style={{
                flex: 1, minWidth: '55px', padding: '7px 4px', borderRadius: '7px', fontWeight: '700', fontSize: '0.75rem', cursor: submitting ? 'not-allowed' : 'pointer',
                background: invoicePaymentMode === m ? (m === 'unpaid' ? '#6b7280' : 'linear-gradient(135deg, #FF4444, #FF6B00)') : 'transparent',
                color: invoicePaymentMode === m ? 'white' : '#555',
                border: invoicePaymentMode === m ? 'none' : '2px solid #e5e7eb',
              }}
            >
              {m === 'unpaid' ? 'Unpaid' : m.toUpperCase()}
            </button>
          ))}
        </div>
        {invoicePaymentMode !== 'unpaid' && (
          <div style={{ marginTop: '5px', fontSize: '0.73rem', color: '#16a34a', fontWeight: '600' }}>
            Will be marked paid via {invoicePaymentMode.toUpperCase()}
          </div>
        )}
      </div>

      {localError && (
        <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', color: '#dc2626', fontSize: '0.85rem' }}>
          {localError}
        </div>
      )}
      <button onClick={createAndInvoice} disabled={submitting} style={{ ...btn('primary', submitting), width: '100%', padding: '13px' }}>
        {submitting ? 'Creating...' : 'Create & Generate Invoice'}
      </button>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────
const BillingPaymentApp = ({ restaurant, managerId, billingSettingsProp }) => {
  const [orders, setOrders] = useState([]);
  const [menu, setMenu] = useState([]);
  const [tables, setTables] = useState([]);
  const [billingSettings, setBillingSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paying, setPaying] = useState(null);
  const [error, setError] = useState('');
  const [printOrder, setPrintOrder] = useState(null);
  const [editingPaymentId, setEditingPaymentId] = useState(null);

  const loadData = useCallback(async () => {
    if (!restaurant?.id) return;
    const [ordersRes, settingsRes, menuRes, tablesRes] = await Promise.all([
      posService.getCounterOrders(restaurant.id, { days: 1 }),
      billingService.getSettings(restaurant.id),
      restaurantService.getMenu(restaurant.id),
      restaurantService.getTables(restaurant.id),
    ]);
    setOrders(ordersRes.data || []);
    setBillingSettings(settingsRes.data);
    setMenu(menuRes.data || []);
    setTables(tablesRes.data || []);
    setLoading(false);
  }, [restaurant?.id]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (billingSettingsProp) setBillingSettings(billingSettingsProp); }, [billingSettingsProp]);
  useEffect(() => {
    const interval = setInterval(loadData, 20000);
    return () => clearInterval(interval);
  }, [loadData]);

  const billable = orders.filter(o => o.status === 'confirmed' && !o.invoice_number && !o.is_invalid);
  const awaitingPayment = orders.filter(o => o.invoice_number && !o.paid_at && o.status !== 'merged' && !o.is_invalid);
  const paidToday = orders.filter(o => o.paid_at && o.status !== 'merged');

  const deleteOrderFn = async (orderId) => {
    const { error: delErr } = await posService.deleteOrder(orderId);
    if (delErr) { setError(friendlyErr(delErr)); return; }
    loadData();
  };

  const markInvalidFn = async (orderId) => {
    await posService.markOrderInvalid(orderId);
    loadData();
  };

  const sendWhatsAppBill = (order) => {
    const phone = (order.customer_phone || '').replace(/\D/g, '');
    if (!phone) return;
    const link = `https://menuron.prismatic-a.com/invoice/${order.id}`;
    const text = encodeURIComponent(`Your bill is ready.\nInvoice: ${order.invoice_number}\nView & download: ${link}`);
    window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
  };

  const changePaymentMethod = async (orderId, method) => {
    await posService.updatePaymentMethod(orderId, method);
    setEditingPaymentId(null);
    loadData();
  };

  const toggleOrder = (orderId) => {
    setSelectedOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
    setError('');
  };

  const selectedOrders = billable.filter(o => selectedOrderIds.has(o.id));

  // Find order numbers of all secondary (merged) orders that share the same invoice
  const getMergedOrderNumbers = (order) =>
    orders
      .filter(o => o.invoice_number === order.invoice_number && o.id !== order.id && o.status === 'merged')
      .map(o => o.order_number)
      .filter(Boolean);

  const recordPayment = async (orderId) => {
    setPaying(orderId); setError('');
    const { error: err } = await posService.markPaid(orderId, paymentMethod);
    setPaying(null);
    if (err) { setError(friendlyErr(err)); return; }
    loadData();
  };

  if (loading) return <div style={{ padding: '60px', textAlign: 'center', color: '#888' }}>Loading billing data...</div>;

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '30px 20px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 4px 0', fontSize: '2rem', fontWeight: '700', color: '#333' }}>Billing & Payment</h1>
        <p style={{ margin: '0', color: '#666' }}>Generate invoices and record payments for today's counter orders.</p>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '10px', padding: '14px 18px', marginBottom: '20px', color: '#dc2626', fontSize: '0.95rem' }}>
          {error}
        </div>
      )}

      {!billingSettings && (
        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '10px', padding: '14px 18px', marginBottom: '20px', color: '#0369a1', fontSize: '0.9rem' }}>
          ℹ️ No billing settings configured — bills will be generated with <strong>GST 0% and Service Charge 0%</strong>. Configure taxes anytime in <strong>Billing Settings</strong>.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' }}>

        {/* LEFT — New order / select existing */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Tab switcher */}
          <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: '12px', padding: '4px', gap: '4px' }}>
            {[{ id: false, label: 'Existing Orders' }, { id: true, label: '＋ New Order' }].map(tab => (
              <button key={String(tab.id)} onClick={() => { setShowNewOrder(tab.id); setSelectedOrderIds(new Set()); setError(''); }}
                style={{ flex: 1, padding: '9px', border: 'none', borderRadius: '9px', fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer', transition: 'all 0.15s', background: showNewOrder === tab.id ? 'white' : 'transparent', color: showNewOrder === tab.id ? '#FF6B00' : '#666', boxShadow: showNewOrder === tab.id ? '0 1px 4px rgba(0,0,0,0.1)' : 'none' }}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* New Order Panel */}
          {showNewOrder && (
            <NewOrderPanel
              menu={menu}
              tables={tables}
              settings={billingSettings}
              restaurantId={restaurant.id}
              timezone={restaurant.timezone || 'Asia/Kolkata'}
              restaurantName={restaurant.name}
              managerId={managerId}
              onDone={(order) => { setShowNewOrder(false); if (order) setPrintOrder({ order, mergedOrderNumbers: [] }); loadData(); }}
              onError={setError}
            />
          )}

          {/* Existing Order selector + billing panel */}
          {!showNewOrder && (<><div style={{ ...card, padding: '24px' }}>
            <h2 style={{ margin: '0 0 6px 0', fontSize: '1.2rem', fontWeight: '700', color: '#333' }}>Select Order to Bill</h2>
            <p style={{ margin: '0 0 14px 0', color: '#888', fontSize: '0.85rem' }}>
              Select one or more orders — selecting multiple merges them into one bill
            </p>

            {selectedOrderIds.size > 1 && (
              <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '8px 12px', marginBottom: '12px', fontSize: '0.82rem', color: '#9a3412', fontWeight: '600' }}>
                {selectedOrderIds.size} orders selected — items will be merged into one bill
              </div>
            )}

            {billable.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px 0', color: '#aaa' }}>
                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🧾</div>
                No unbilled orders — confirm orders in the Orders tab first.
              </div>
            ) : (
              billable.map(order => {
                const isSelected = selectedOrderIds.has(order.id);
                return (
                  <div
                    key={order.id}
                    onClick={() => toggleOrder(order.id)}
                    style={{
                      border: isSelected ? '2px solid #FF6B00' : '2px solid #e5e7eb',
                      borderRadius: '10px', padding: '14px', marginBottom: '10px',
                      cursor: 'pointer', background: isSelected ? '#fff7ed' : 'white',
                      transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: '12px',
                    }}
                  >
                    {/* Checkbox */}
                    <div style={{
                      width: '20px', height: '20px', borderRadius: '4px', flexShrink: 0,
                      border: isSelected ? 'none' : '2px solid #d1d5db',
                      background: isSelected ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {isSelected && <span style={{ color: 'white', fontSize: '0.7rem', fontWeight: '900' }}>✓</span>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '700', color: '#333' }}>
                        {order.order_number || '—'}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '2px' }}>
                        {order.restaurant_tables && <span style={{ fontWeight: '600', color: '#555' }}>{order.restaurant_tables.custom_name || `Table ${order.restaurant_tables.table_number}`} · </span>}
                        {new Date(order.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} · {new Date(order.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} · {order.order_items?.length} item(s)
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ fontWeight: '700', color: '#FF4444' }}>{fmt(order.total_amount)}</div>
                      <button
                        onClick={e => { e.stopPropagation(); if (window.confirm('Delete this order? This cannot be undone.')) deleteOrderFn(order.id); }}
                        style={{ background: 'transparent', border: '1px solid #fca5a5', borderRadius: '6px', padding: '3px 8px', fontSize: '0.75rem', color: '#dc2626', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}
                      >Delete</button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {selectedOrders.length > 0 && (
            <BillingPanel
              orders={selectedOrders}
              menu={menu}
              settings={billingSettings}
              restaurantId={restaurant.id}
              timezone={restaurant.timezone || 'Asia/Kolkata'}
              managerId={managerId}
              onInvoiceGenerated={() => { setSelectedOrderIds(new Set()); loadData(); }}
              onError={setError}
            />
          )}
          </>)}
        </div>

        {/* RIGHT — Awaiting payment + paid today */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Awaiting payment */}
          <div style={{ ...card, padding: '24px' }}>
            <h2 style={{ margin: '0 0 6px 0', fontSize: '1.2rem', fontWeight: '700', color: '#333' }}>Awaiting Payment</h2>
            <p style={{ margin: '0 0 14px 0', color: '#888', fontSize: '0.85rem' }}>Select payment method and mark as paid</p>

            {awaitingPayment.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px 0', color: '#aaa' }}>
                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>✅</div>
                No pending payments
              </div>
            ) : (
              awaitingPayment.map(order => (
                <div key={order.id} style={{ border: '2px solid #e5e7eb', borderRadius: '12px', padding: '16px', marginBottom: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <div>
                      <div style={{ fontWeight: '700', color: '#333' }}>
                        {order.order_number || '—'}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#888' }}>
                        {order.restaurant_tables && <span style={{ fontWeight: '600', color: '#555' }}>{order.restaurant_tables.custom_name || `Table ${order.restaurant_tables.table_number}`} · </span>}
                        {order.invoice_number}
                      </div>
                      {order.overridden_at && (
                        <div style={{ fontSize: '0.75rem', color: '#92400e', marginTop: '2px' }}>⚠️ Override logged</div>
                      )}
                    </div>
                    <div>
                      <div style={{ fontWeight: '800', fontSize: '1.1rem', color: '#FF4444', textAlign: 'right' }}>{fmt(order.total_payable)}</div>
                      {order.discount_amount > 0 && (
                        <div style={{ fontSize: '0.75rem', color: '#16a34a', textAlign: 'right' }}>−{fmt(order.discount_amount)} disc.</div>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                    {PAYMENT_METHODS.map(m => (
                      <button
                        key={m}
                        onClick={() => setPaymentMethod(m)}
                        style={{
                          flex: 1, padding: '7px 4px', borderRadius: '8px', fontWeight: '600', fontSize: '0.78rem', cursor: 'pointer',
                          background: paymentMethod === m ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : 'transparent',
                          color: paymentMethod === m ? 'white' : '#555',
                          border: paymentMethod === m ? 'none' : '2px solid #e5e7eb',
                        }}
                      >{m.toUpperCase()}</button>
                    ))}
                  </div>

                  <button
                    onClick={() => recordPayment(order.id)}
                    disabled={paying === order.id}
                    style={{ ...btn('green', paying === order.id), width: '100%' }}
                  >
                    {paying === order.id ? 'Recording...' : `Mark Paid — ${paymentMethod.toUpperCase()}`}
                  </button>
                  <button
                    onClick={() => setPrintOrder({ order, mergedOrderNumbers: getMergedOrderNumbers(order) })}
                    style={{ ...btn('outline'), width: '100%', marginTop: '8px', fontSize: '0.85rem' }}
                  >
                    Print / Download Invoice
                  </button>
                  {order.customer_phone && (
                    <button
                      onClick={() => sendWhatsAppBill(order)}
                      style={{ background: '#25D366', border: 'none', borderRadius: '10px', padding: '8px', width: '100%', marginTop: '8px', fontSize: '0.85rem', color: 'white', cursor: 'pointer', fontWeight: '700' }}
                    >
                      Send Bill on WhatsApp
                    </button>
                  )}
                  <button
                    onClick={() => { if (window.confirm(`Mark invoice ${order.invoice_number} as invalid? This cannot be undone.`)) markInvalidFn(order.id); }}
                    style={{ background: 'transparent', border: '1.5px solid #fca5a5', borderRadius: '10px', padding: '8px', width: '100%', marginTop: '8px', fontSize: '0.85rem', color: '#dc2626', cursor: 'pointer', fontWeight: '700' }}
                  >
                    Mark as Invalid
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Paid today summary */}
          <div style={{ ...card, padding: '24px' }}>
            <h2 style={{ margin: '0 0 14px 0', fontSize: '1.2rem', fontWeight: '700', color: '#333' }}>Paid Today</h2>
            {paidToday.length === 0 ? (
              <p style={{ color: '#aaa', textAlign: 'center', padding: '20px 0', margin: 0 }}>No completed payments yet</p>
            ) : (
              <>
                {paidToday.map(order => {
                  const isInvalid = !!order.is_invalid;
                  return (
                  <div key={order.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f3f4f6', fontSize: '0.9rem', opacity: isInvalid ? 0.6 : 1 }}>
                    <div>
                      <div style={{ fontWeight: '600', color: isInvalid ? '#999' : '#333', textDecoration: isInvalid ? 'line-through' : 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {order.invoice_number}
                        {isInvalid && <span style={{ background: '#fee2e2', color: '#991b1b', fontSize: '0.65rem', fontWeight: '700', padding: '1px 5px', borderRadius: '4px', textDecoration: 'none' }}>INVALID</span>}
                      </div>
                      <div style={{ color: '#888', fontSize: '0.78rem' }}>
                        {order.order_number && <span style={{ fontWeight: '600', color: '#555' }}>{order.order_number} · </span>}
                        {order.payment_method?.toUpperCase()} · {new Date(order.paid_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} · {new Date(order.paid_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        {order.overridden_at ? ' · ⚠️ override' : ''}
                      </div>
                      {!isInvalid && editingPaymentId === order.id && (
                        <div style={{ display: 'flex', gap: '5px', marginTop: '8px', flexWrap: 'wrap' }}>
                          {PAYMENT_METHODS.map(m => (
                            <button key={m} onClick={() => changePaymentMethod(order.id, m)} style={{
                              padding: '4px 10px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', border: 'none',
                              background: order.payment_method === m ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : '#f3f4f6',
                              color: order.payment_method === m ? 'white' : '#555',
                            }}>{m.toUpperCase()}</button>
                          ))}
                          <button onClick={() => setEditingPaymentId(null)} style={{ padding: '4px 8px', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer', border: '1px solid #e5e7eb', background: 'white', color: '#888' }}>✕</button>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                      <div style={{ fontWeight: '700', color: isInvalid ? '#bbb' : '#16a34a', textDecoration: isInvalid ? 'line-through' : 'none' }}>{fmt(order.total_payable)}</div>
                      {!isInvalid && (
                        <>
                          <button
                            onClick={() => setEditingPaymentId(editingPaymentId === order.id ? null : order.id)}
                            style={{ background: 'transparent', border: '1px solid #d1d5db', borderRadius: '6px', padding: '4px 10px', fontSize: '0.78rem', color: '#555', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}
                          >Edit Payment</button>
                          <button
                            onClick={() => setPrintOrder({ order, mergedOrderNumbers: getMergedOrderNumbers(order) })}
                            style={{ background: 'transparent', border: '1px solid #d1d5db', borderRadius: '6px', padding: '4px 10px', fontSize: '0.78rem', color: '#555', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}
                          >Print</button>
                          {order.customer_phone && (
                            <button
                              onClick={() => sendWhatsAppBill(order)}
                              style={{ background: '#25D366', border: 'none', borderRadius: '6px', padding: '4px 10px', fontSize: '0.78rem', color: 'white', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}
                            >WhatsApp</button>
                          )}
                          <button
                            onClick={() => { if (window.confirm(`Mark invoice ${order.invoice_number} as invalid? This cannot be undone.`)) markInvalidFn(order.id); }}
                            style={{ background: 'transparent', border: '1px solid #fca5a5', borderRadius: '6px', padding: '4px 10px', fontSize: '0.78rem', color: '#dc2626', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}
                          >Mark as Invalid</button>
                        </>
                      )}
                    </div>
                  </div>
                  );
                })}

                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', fontWeight: '800', color: '#333' }}>
                  <span>Total Collected</span>
                  <span style={{ color: '#16a34a' }}>{fmt(paidToday.filter(o => !o.is_invalid).reduce((s, o) => s + (o.total_payable || 0), 0))}</span>
                </div>

                {/* Per-method breakdown */}
                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f3f4f6' }}>
                  {PAYMENT_METHODS.filter(m => paidToday.some(o => o.payment_method === m && !o.is_invalid)).map(m => (
                    <div key={m} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#666', padding: '2px 0' }}>
                      <span>{m.toUpperCase()}</span>
                      <span style={{ fontWeight: '600' }}>{fmt(paidToday.filter(o => o.payment_method === m && !o.is_invalid).reduce((s, o) => s + (o.total_payable || 0), 0))}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {printOrder && (
        <InvoicePrint
          order={printOrder.order}
          mergedOrderNumbers={printOrder.mergedOrderNumbers}
          restaurant={restaurant}
          billingSettings={billingSettings}
          onClose={() => setPrintOrder(null)}
        />
      )}
    </div>
  );
};

export default BillingPaymentApp;
